import { useEffect, useRef, useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import config from 'src/config'
import Link from 'src/components/Link'
import AiGeneratedBadge from 'src/components/AiGeneratedBadge'
import { trackRecommendationClick } from 'src/functions/commerceTracking'
import { post } from 'src/functions/fetch'
import { productVariantUrl } from 'src/functions/productUrl'
import { getCartPriceAmount } from 'src/functions/cartPriceLock'
import formatEuro from 'src/functions/formatEuro'
import { useDiscountDateActive } from 'src/hooks/useDiscountDateActive'
import getImageUrl from 'src/pages/Product/functions/imageUrl'
import getImageAlt from 'src/pages/Product/functions/imageAlt'
import { useDispatch, useSelector } from 'src/redux/hooks'
import { isCrossSellSections, isProduct } from 'src/shared/frontendTypes/product'
import type { tProduct } from 'src/shared/frontendTypes/product'
import type { tItem } from 'src/shared/frontendTypes/warenkorb'
import { buildRecommendationCartItem, recommendationPrice, sameProductComplements, selectCartRecommendations, type CartRecommendation, type CartRecommendationSection } from './recommendations'
import styles from './ModernCart.module.css'

const RecommendationPrice = ({ price, onlyStock, stock }: Pick<tItem, 'price' | 'onlyStock' | 'stock'>) => {
  const discountDateActive = useDiscountDateActive(typeof price === 'number' ? undefined : price.dateEnd)
  const total = getCartPriceAmount(price, 1, { onlyStock, stock, ignoreQuantityDiscount: !discountDateActive })
  const regular = typeof price === 'number' ? total : price.regularPrice
  const saving = typeof price !== 'number' && price.type !== 'invisible'
    ? Math.max(0, Math.round((regular - total) * 100) / 100) : 0
  const percent = saving > 0 && regular > 0 && typeof price !== 'number' && price.showPercent !== false
    ? Math.round(saving / regular * 100) : 0

  return <div className={`${styles.priceLine} ${styles.recommendationPrice}`}>
    {saving > 0 && <del>{formatEuro(regular)}</del>}
    {percent > 0 && <span className={styles.discount}>−{percent} %</span>}
    <strong>{formatEuro(total)}</strong>
  </div>
}

const CartRecommendations = () => {
  const items = useSelector(store => store.Warenkorb.items)
  const dispatch = useDispatch()
  const itemsRef = useRef(items)
  itemsRef.current = items
  const requestItems = Object.values(items).slice(0, 4).map(({ id, key, attributes }) => ({
    id, key, options: Object.values(attributes || {}).sort((a, b) => a - b),
  }))
  const selectionKey = JSON.stringify(requestItems)
  const [response, setResponse] = useState<{ key: string; recommendations: CartRecommendation[] }>()
  const [busy, setBusy] = useState<string | null>(null)
  const [message, setMessage] = useState<{ text: string; error?: boolean }>()
  const [updatedPrices, setUpdatedPrices] = useState<Record<string, tItem>>({})
  const addRequest = useRef<AbortController>()

  useEffect(() => {
    const controller = new AbortController()
    const sourceItems = JSON.parse(selectionKey) as typeof requestItems
    const sourceProducts = new Map<number, Promise<tProduct | null>>()
    const loadProduct = (id: number) => {
      if (!sourceProducts.has(id)) sourceProducts.set(id, post({
        url: `product/${id}`,
        body: { ...config.shop, skipAlsoPurchased: true, skipCrossSellSections: true, skipBreadcrumb: true },
        signal: controller.signal,
      }).then(data => isProduct(data) && data.id === id ? data : null).catch(() => null))
      return sourceProducts.get(id)!
    }
    const sections: CartRecommendationSection[][] = sourceItems.map(() => [])
    const complements: CartRecommendation[][] = sourceItems.map(() => [])
    let published: CartRecommendation[] = []
    const identity = ({ product }: CartRecommendation) => `${product.id}:${product.key}`
    const publish = () => {
      if (controller.signal.aborted) return
      const available = selectCartRecommendations(sections.flat(), itemsRef.current, 4, complements.flat())
      const byId = new Map(available.map(item => [identity(item), item]))
      // Preserve the priority of same-product complements, and the relative
      // order of cards that remain among the best four when more results arrive.
      const retained = published.map(item => byId.get(identity(item))).filter((item): item is CartRecommendation => !!item)
      const retainedIds = new Set(retained.map(identity))
      published = [...retained, ...available.filter(item => !retainedIds.has(identity(item)))].slice(0, 4)
      setResponse({ key: selectionKey, recommendations: published })
    }
    sourceItems.forEach((source, index) => {
      // Neither a slow source product nor a second cart item blocks ready cards.
      void post({
        url: `product/${source.id}/cross-sell`,
        body: { ...config.shop, selectedOptions: source.options, arts: ['serie', 'zubehoer', 'ergaenzung'] },
        signal: controller.signal,
      }).then(data => {
        sections[index] = isCrossSellSections(data) ? data.map(section => ({ ...section, recommendation: {
          surface: 'cart', sourceProductId: source.id, sourceVariantKey: source.key,
          section: section.art === 'serie' || section.art === 'zubehoer' || section.art === 'ergaenzung' ? section.art : 'other',
        } })) : []
        publish()
      }).catch(() => undefined)
      void loadProduct(source.id).then(product => {
        complements[index] = product ? sameProductComplements(product, source.key) : []
        publish()
      }).catch(() => undefined)
    })
    return () => controller.abort()
  }, [selectionKey])

  useEffect(() => () => addRequest.current?.abort(), [])

  const current = response?.key === selectionKey ? response : undefined
  const recommendations = selectCartRecommendations([], items, 4, current?.recommendations)
  const add = async ({ product, recommendation }: CartRecommendation) => {
    if (addRequest.current && !addRequest.current.signal.aborted) return
    const controller = new AbortController()
    addRequest.current = controller
    setBusy(product.key)
    setMessage(undefined)
    try {
      const data = await post({
        url: `product/${product.id}`,
        body: { ...config.shop, skipAlsoPurchased: true, skipCrossSellSections: true, skipBreadcrumb: true },
        signal: controller.signal,
      })
      if (controller.signal.aborted) return
      const item = isProduct(data) && data.id === product.id
        ? buildRecommendationCartItem(data, product.key, itemsRef.current) : null
      if (!item) {
        setMessage({ text: 'Diese Variante ist gerade nicht verfügbar. Bitte wähle eine andere Ergänzung.', error: true })
        return
      }
      const shown = updatedPrices[product.key]
      const shownPrice = getCartPriceAmount(shown?.price ?? recommendationPrice(product), 1, shown || product)
      if (Math.abs(getCartPriceAmount(item.price, 1, item) - shownPrice) > .005) {
        setUpdatedPrices(previous => ({ ...previous, [product.key]: item }))
        setMessage({ text: 'Der Preis wurde aktualisiert. Bitte prüfe ihn und füge den Artikel erneut hinzu.' })
        return
      }
      dispatch({ type: 'addWarenkorbItem', payload: item, ...(recommendation ? { meta: { recommendation } } : {}) })
      setMessage({ text: `${item.name} wurde zum Warenkorb hinzugefügt.` })
    } catch (_error) {
      if (!controller.signal.aborted) setMessage({ text: 'Der Artikel konnte gerade nicht hinzugefügt werden. Bitte versuche es noch einmal.', error: true })
    } finally {
      if (!controller.signal.aborted) setBusy(null)
      controller.abort()
    }
  }

  if (!recommendations.length && !message) return null

  return <section className={styles.recommendations} aria-label='Empfehlungen zu deinem Warenkorb'>
    {recommendations.length > 0 && <>
      <p className={styles.recommendationLabel}>Unsere Empfehlungen</p>
      <h2 className={styles.recommendationHeading}>Passt perfekt dazu</h2>
      <p className={styles.recommendationIntro}>Passende Ergänzungen – noch nicht in deinem Warenkorb.</p>
      <div className={styles.recommendationGrid}>
        {recommendations.map(recommendation => {
          const { product, reason } = recommendation
          const updated = updatedPrices[product.key]
          const href = productVariantUrl(product.id, product.path, product.key)
          const trackClick = (event: React.MouseEvent) => trackRecommendationClick(event.nativeEvent, { id: product.id, key: product.key }, recommendation.recommendation)
          const tracking = { onClick: trackClick, onAuxClick: (event: React.MouseEvent) => { if (event.button === 1) trackClick(event) } }
          return <article key={`${product.id}:${product.key}`} className={styles.recommendationCard} aria-label={product.name}>
            <div className={styles.recommendationImage}>
              <Link href={href} {...tracking} aria-label={product.name}>
                <img src={getImageUrl(product.images[0], product.id, 'sm') || getImageUrl(product.images[0], product.id, 'xs') || undefined} alt={getImageAlt(product.images[0], product.name)} width={80} height={104} loading='lazy' decoding='async' />
              </Link>
              <AiGeneratedBadge aiGenerated={product.images[0]?.aiGenerated} />
            </div>
            <div className={styles.recommendationDetails}>
              <p className={styles.recommendationReason}>{reason}</p>
              <h3 className={styles.recommendationTitle}><Link href={href} {...tracking}>{product.name}</Link></h3>
              <p className={styles.recommendationAttributes}>{product.attributes.join(' · ')}</p>
              <RecommendationPrice price={updated?.price ?? recommendationPrice(product)} onlyStock={updated?.onlyStock ?? product.onlyStock} stock={updated?.stock ?? product.stock} />
              <Button variant='outlined' color='inherit' size='small' sx={{
                alignSelf: 'flex-start', mt: 1.25, px: 1.5, minHeight: 40, borderRadius: 1,
                color: 'var(--page-builder-text, #102d59)', borderColor: '#c9c4bc',
                fontSize: '.8rem', fontWeight: 600, textTransform: 'none', boxShadow: 'none',
                '&:hover': { borderColor: 'var(--page-builder-text, #102d59)', bgcolor: '#f7f3ed', boxShadow: 'none' },
              }} disabled={busy !== null} onClick={() => void add(recommendation)} aria-label={`${product.name} ${product.attributes.join(', ')} hinzufügen`}>
                {busy === product.key ? 'Wird hinzugefügt …' : 'Hinzufügen'}
              </Button>
            </div>
          </article>
        })}
      </div>
    </>}
    {message && <Alert severity={message.error ? 'warning' : 'success'} sx={{ mt: 1.5 }} role='status'>{message.text}</Alert>}
  </section>
}

export default CartRecommendations
